// SessionManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdAdd,
  MdArrowForward,
  MdClass,
  MdEdit,
  MdDelete,
  MdSearch,
} from "react-icons/md";
import {
  API_BASE_URL,
  getAuthHeaders,
  getJsonAuthHeaders,
} from "../../../utils/api";
import { confirmDialog } from "../../../utils/dialogs";
import { C, Card, CardHeader, TH, TD, Badge, Btn } from "../../shared/fees/feeUI";
import { StatCard, StatGrid, BackButton } from "../../shared/reports/reportsShared";

const labelSt = { display: "block", fontSize: "11px", fontWeight: 700, color: C.slate, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" };
const inputSt = { padding: "9px 13px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", color: C.text, background: C.white, outline: "none", boxSizing: "border-box" };

const stageBadgeColor = (className) => ({
  upcoming: "blue",
  "in-progress": "green",
  completed: "slate",
  muted: "slate",
}[className] || "slate");

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getSessionStage = (session) => {
  const start = getDate(session.start_date);
  const end = getDate(session.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!start || !end) return { label: "Dates pending", className: "muted" };
  if (today < start) return { label: "Upcoming", className: "upcoming" };
  if (today > end) return { label: "Completed", className: "completed" };
  return { label: "In progress", className: "in-progress" };
};

const getSessionLength = (session) => {
  const start = getDate(session.start_date);
  const end = getDate(session.end_date);

  if (!start || !end) return "Duration pending";

  const days = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / 86400000) + 1,
  );

  return `${days} day${days === 1 ? "" : "s"}`;
};

function SessionManagement({ campusId, campusName, onBack, showToast, onLogout }) {
  const navigate = useNavigate();
  const isSuperadmin =
    (localStorage.getItem("als_role") || "").toLowerCase() === "superadmin";
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [newSession, setNewSession] = useState({
    name: "",
    start_date: "",
    end_date: "",
    is_active: true,
  });
  const [sessionForm, setSessionForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    is_active: true,
  });

  const getErrorMessage = useCallback(async (res, fallback) => {
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data.detail)) {
      return data.detail.map((err) => err.msg).join(", ") || fallback;
    }
    return data.detail || fallback;
  }, []);

  const loadSessions = useCallback(async () => {
    if (!campusId) return;
    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/sessions?campus_id=${campusId}`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, "Could not load sessions."));
      }
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load sessions", error);
      showToast?.(
        "error",
        "Load Error",
        error.message || "Could not load sessions.",
      );
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [campusId, getErrorMessage, onLogout, showToast]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleCreateSession = async (e) => {
    e.preventDefault();

    if (!newSession.name || !newSession.start_date || !newSession.end_date) {
      showToast("error", "Error", "Please fill in all required fields.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/campuses/${campusId}/sessions`,
        {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({
            name: newSession.name,
            start_date: newSession.start_date,
            end_date: newSession.end_date,
            is_active: newSession.is_active,
          }),
        },
      );
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(
          await getErrorMessage(res, "Could not create session."),
        );
      }

      showToast(
        "success",
        "Session created",
        `${newSession.name} has been added.`,
      );
      setShowAddModal(false);
      setNewSession({
        name: "",
        start_date: "",
        end_date: "",
        is_active: true,
      });
      await loadSessions();
    } catch (error) {
      console.error("Failed to create session", error);
      showToast?.(
        "error",
        "Create Error",
        error.message || "Could not create session.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    if (!editingSession) return;

    setIsSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/sessions/${editingSession.id}`,
        {
          method: "PUT",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({
            name: sessionForm.name,
            start_date: sessionForm.start_date,
            end_date: sessionForm.end_date,
            is_active: sessionForm.is_active,
          }),
        },
      );
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(
          await getErrorMessage(res, "Could not update session."),
        );
      }

      showToast(
        "success",
        "Session updated",
        "Session details have been updated.",
      );
      setEditingSession(null);
      setSessionForm({
        name: "",
        start_date: "",
        end_date: "",
        is_active: true,
      });
      await loadSessions();
    } catch (error) {
      console.error("Failed to update session", error);
      showToast?.(
        "error",
        "Update Error",
        error.message || "Could not update session.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    const confirmed = await confirmDialog(
      "Are you sure you want to delete this session?",
      {
        title: "Delete session",
        confirmLabel: "Delete session",
        variant: "danger",
      },
    );

    if (!confirmed) return;

    setDeletingSessionId(sessionId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(
          await getErrorMessage(res, "Could not delete session."),
        );
      }

      showToast("success", "Session deleted", "Session removed successfully.");
      await loadSessions();
    } catch (error) {
      console.error("Failed to delete session", error);
      showToast?.(
        "error",
        "Delete Error",
        error.message || "Could not delete session.",
      );
    } finally {
      setDeletingSessionId(null);
    }
  };

  const filteredSessions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sessions
      .filter((session) => {
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && session.is_active) ||
          (statusFilter === "inactive" && !session.is_active);

        const searchableText = [
          session.name,
          session.start_date,
          session.end_date,
          getSessionStage(session).label,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return matchesStatus && searchableText.includes(normalizedSearch);
      })
      .sort((a, b) => {
        const first = getDate(a.start_date)?.getTime() || 0;
        const second = getDate(b.start_date)?.getTime() || 0;
        return second - first;
      });
  }, [sessions, searchTerm, statusFilter]);

  const activeSessionCount = sessions.filter((s) => s.is_active).length;
  const inactiveSessionCount = sessions.length - activeSessionCount;

  const handleViewClasses = (sessionId) => {
    navigate(`/superadmin/campuses/${campusId}/classes?session_id=${sessionId}`);
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
        <BackButton onClick={onBack} label="Back" />
        <div style={{ marginLeft: "auto" }}>
          <Btn onClick={() => setShowAddModal(true)} disabled={isSaving}>
            <MdAdd /> {isSaving ? "Please wait…" : "Add Session"}
          </Btn>
        </div>
      </div>

      {/* Stats */}
      <StatGrid>
        <StatCard label="Total Sessions" value={sessions.length} accent={C.navy} />
        <StatCard label="Active" value={activeSessionCount} accent={C.green} />
        <StatCard label="Inactive" value={inactiveSessionCount} accent={C.slateLight} />
      </StatGrid>

      {/* Filters */}
      <Card style={{ marginBottom: "16px" }}>
        <div style={{ padding: "16px 20px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 260px" }}>
            <label style={labelSt}>Search Sessions</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.slateLight, display: "flex", pointerEvents: "none" }}>
                <MdSearch />
              </span>
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, dates, or stage"
                style={{ ...inputSt, width: "100%", paddingLeft: "36px" }}
              />
            </div>
          </div>
          <div style={{ minWidth: "180px" }}>
            <label style={labelSt}>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputSt}>
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader
          title="Sessions"
          right={<Badge color="slate">{filteredSessions.length} of {sessions.length} shown</Badge>}
        />

        {filteredSessions.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <TH>Session</TH>
                  <TH>Start Date</TH>
                  <TH>End Date</TH>
                  <TH>Duration</TH>
                  <TH>Stage</TH>
                  <TH>Status</TH>
                  <TH right>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session, i) => {
                  const stage = getSessionStage(session);

                  return (
                    <tr key={session.id} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                      <TD><Badge color="blue">{session.name}</Badge></TD>
                      <TD>{formatDate(session.start_date)}</TD>
                      <TD>{formatDate(session.end_date)}</TD>
                      <TD>{getSessionLength(session)}</TD>
                      <TD><Badge color={stageBadgeColor(stage.className)}>{stage.label}</Badge></TD>
                      <TD>
                        <Badge color={session.is_active ? "green" : "red"}>
                          {session.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TD>
                      <TD right>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <Btn size="sm" variant="blue" onClick={() => handleViewClasses(session.id)}>
                            <MdClass /> View classes <MdArrowForward />
                          </Btn>
                          <Btn
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingSession(session);
                              setSessionForm({
                                name: session.name,
                                start_date: session.start_date,
                                end_date: session.end_date,
                                is_active: session.is_active,
                              });
                            }}
                          >
                            <MdEdit /> Edit
                          </Btn>
                          {isSuperadmin && (
                            <Btn
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteSession(session.id)}
                              disabled={deletingSessionId === session.id}
                            >
                              <MdDelete /> {deletingSessionId === session.id ? "Deleting…" : "Delete"}
                            </Btn>
                          )}
                        </div>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {isLoading && sessions.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>Loading sessions</div>
            <p style={{ fontSize: "13px" }}>Please wait while we fetch sessions for this campus.</p>
          </div>
        )}

        {!isLoading && sessions.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No sessions created yet</div>
            <p style={{ fontSize: "13px", marginBottom: "16px" }}>
              Start by creating the first academic session for {campusName || "this campus"}.
            </p>
            <Btn onClick={() => setShowAddModal(true)}>
              <MdAdd /> Create session
            </Btn>
          </div>
        )}

        {!isLoading && sessions.length > 0 && filteredSessions.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No matching sessions</div>
            <p style={{ fontSize: "13px" }}>Try adjusting your search text or status filter.</p>
          </div>
        )}
      </Card>

      {/* Add Session Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content campus-form-modal">
            <div className="modal-header">
              <h3>Add new session</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateSession}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Session name *</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={newSession.name}
                    onChange={(e) =>
                      setNewSession({ ...newSession, name: e.target.value })
                    }
                    required
                    placeholder="e.g. Academic Year 2026–2027"
                  />
                </div>
                <div className="reg-form-row">
                  <div className="reg-form-group">
                    <label className="reg-form-label">Start date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={newSession.start_date}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          start_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="reg-form-group">
                    <label className="reg-form-label">End date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={newSession.end_date}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          end_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="reg-form-group checkbox-group">
                  <label className="reg-form-label checkbox-label">
                    <input
                      type="checkbox"
                      checked={newSession.is_active}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    Active session
                  </label>
                  <span className="checkbox-helper">
                    Active sessions are highlighted in dashboards and filters.
                  </span>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAddModal(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Creating…" : "Create session"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {editingSession && (
        <div className="modal-overlay">
          <div className="modal-content campus-form-modal">
            <div className="modal-header">
              <h3>Edit session</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setEditingSession(null)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateSession}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Session name *</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={sessionForm.name}
                    onChange={(e) =>
                      setSessionForm({ ...sessionForm, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="reg-form-row">
                  <div className="reg-form-group">
                    <label className="reg-form-label">Start date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={sessionForm.start_date}
                      onChange={(e) =>
                        setSessionForm({
                          ...sessionForm,
                          start_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="reg-form-group">
                    <label className="reg-form-label">End date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={sessionForm.end_date}
                      onChange={(e) =>
                        setSessionForm({
                          ...sessionForm,
                          end_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="reg-form-group checkbox-group">
                  <label className="reg-form-label checkbox-label">
                    <input
                      type="checkbox"
                      checked={sessionForm.is_active}
                      onChange={(e) =>
                        setSessionForm({
                          ...sessionForm,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    Active session
                  </label>
                  <span className="checkbox-helper">
                    Disable this if the session is no longer active.
                  </span>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setEditingSession(null)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Updating…" : "Update session"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionManagement;